export type ParsedSharePointUrl = {
  hostname: string
  siteName: string
  listName: string
  folderPath: string
}

/**
 * Parse a SharePoint URL into hostname, site name, library name, and optional folder path.
 *
 * Supports both URL forms:
 *   Direct:  https://tenant.sharepoint.com/sites/<site>/<library>/<folder>/<sub>
 *   Sharing: https://tenant.sharepoint.com/:f:/r/sites/<site>/<library>/<folder>/...?e=...
 *
 * Only `:f:` (folder) sharing links yield a folderPath; other prefixes
 * (:b: file, :w: Word, :x: Excel, :p: PowerPoint) fall back to library-level.
 */
export function parseSharePointUrl(url: string): ParsedSharePointUrl | null {
  try {
    const parsedUrl = new URL(url)
    if (!parsedUrl.hostname.endsWith(".sharepoint.com")) return null
    const hostname = parsedUrl.hostname

    const rawParts = parsedUrl.pathname.split("/").filter(Boolean)
    if (rawParts.length === 0) return null

    // Detect sharing-link prefix like `:f:/r/`, `:b:/r/`, `:w:/r/`, `:x:/r/`, `:p:/r/`.
    const sharingPrefixMatch = /^:([a-z]):$/.exec(rawParts[0])
    let isFolderShare = false
    let pathParts = rawParts
    if (sharingPrefixMatch && rawParts.length >= 3 && rawParts[1] === "r") {
      isFolderShare = sharingPrefixMatch[1] === "f"
      pathParts = rawParts.slice(2)
    }

    if (pathParts.length < 2 || pathParts[0] !== "sites") return null

    const siteName = decodeURIComponent(pathParts[1])

    // Segments after the site, minus the view page a browser URL ends with
    // (`.../Shared Documents/Forms/AllItems.aspx`, `.../Lists/<list>/AllItems.aspx`): a copied
    // browser URL must parse like the plain library URL.
    let rest = pathParts.slice(2)
    if (rest.length > 0 && /\.aspx$/i.test(rest[rest.length - 1])) {
      rest = rest.slice(0, -1)
      if (rest.length > 0 && /^forms$/i.test(rest[rest.length - 1])) rest = rest.slice(0, -1)
    }

    // Generic lists live under `/Lists/<name>`: the list is the segment after `Lists`.
    let listName = "Shared Documents"
    if (rest.length >= 2 && /^lists$/i.test(rest[0])) {
      listName = decodeURIComponent(rest[1])
      rest = rest.slice(2)
    } else if (rest.length >= 1) {
      listName = decodeURIComponent(rest[0])
      rest = rest.slice(1)
    }

    // Only extract folder path for direct URLs or `:f:/r/` folder shares.
    const canHaveFolder = !sharingPrefixMatch || isFolderShare
    const folderPath =
      canHaveFolder && rest.length > 0 ? rest.map(decodeURIComponent).join("/") : ""

    return { hostname, siteName, listName, folderPath }
  } catch {
    return null
  }
}
